import type { Cheerio, CheerioAPI } from 'cheerio'
import type { AnyNode } from 'domhandler'
import type { CSSObject } from 'styled-components'
import type { GetThemeNormalized } from '@messenger/types'

import { load } from 'cheerio'
import { withScriptFallback } from '@messenger/lotus/fonts'
import { preserveStylingUtilities } from './preserveStylingUtilities'

type ApplyThemeStylesToServiceTemplateProps = {
  html: string | null | undefined
  selectedTheme?: GetThemeNormalized | null
}

type ThemeStyles = {
  font?: string | null
  color?: string | null
  style?: string | null
  case?: string | null
}

type ApplyStylesToElementAndChildrenProps = {
  element: Cheerio<AnyNode>
  $: CheerioAPI
  styles: ThemeStyles
  parseCSS: (css: string | null) => CSSObject
  stringifyCSS: (cssObject: CSSObject) => string
}

type ApplyStylesToSingleElementProps = {
  element: Cheerio<AnyNode>
  styles: ThemeStyles
  parseCSS: (css: string | null) => CSSObject
  stringifyCSS: (cssObject: CSSObject) => string
}

export const applyThemeStylesToServiceTemplate = ({
  html,
  selectedTheme,
}: ApplyThemeStylesToServiceTemplateProps): string | null | undefined => {
  if (!html || !selectedTheme) {
    return html
  }

  const $ = load(html)
  const { parseCSS, stringifyCSS } = preserveStylingUtilities()

  // Apply theme styles to headers and service templates body text
  $('[data-template]').each(function () {
    const element = $(this)
    const templateKey = element.attr('data-template')

    // Apply theme styles to headers
    if (templateKey && templateKey.toLowerCase().endsWith('header')) {
      applyStylesToElementAndChildren({
        element,
        $,
        styles: {
          font: selectedTheme.header_font,
          color: selectedTheme.header_color,
          style: selectedTheme.header_style,
          case: selectedTheme.header_case,
        },
        parseCSS,
        stringifyCSS,
      })
    } else {
      // Apply theme styles to service templates body text
      applyStylesToElementAndChildren({
        element,
        $,
        styles: {
          font: selectedTheme.body_font,
          color: selectedTheme.body_color,
          style: selectedTheme.body_style,
          case: selectedTheme.body_case,
        },
        parseCSS,
        stringifyCSS,
      })
    }
  })

  // Apply theme styles case details
  $('[data-case-detail]').each(function () {
    const element = $(this)
    const caseDetailKey = element.attr('data-case-detail')

    const isNameCaseDetail =
      caseDetailKey === 'name_of_deceased' ||
      caseDetailKey === 'prefix_dec' ||
      caseDetailKey === 'first_dec' ||
      caseDetailKey === 'nick_name_dec' ||
      caseDetailKey === 'middle_dec' ||
      caseDetailKey === 'maiden_dec' ||
      caseDetailKey === 'lastdec' ||
      caseDetailKey === 'suffix_dec'

    // Apply theme styles to name of deceased and other name components
    if (isNameCaseDetail) {
      applyStylesToElementAndChildren({
        element,
        $,
        styles: {
          font: selectedTheme.name_of_deceased_font,
          color: selectedTheme.name_of_deceased_color,
          style: selectedTheme.name_of_deceased_style,
          case: selectedTheme.name_of_deceased_case,
        },
        parseCSS,
        stringifyCSS,
      })
    } else {
      // Apply theme styles to other case details
      applyStylesToElementAndChildren({
        element,
        $,
        styles: {
          font: selectedTheme.body_font,
          color: selectedTheme.body_color,
          style: selectedTheme.body_style,
          case: selectedTheme.body_case,
        },
        parseCSS,
        stringifyCSS,
      })
    }
  })

  $('[data-case-detail-table]').each(function () {
    const element = $(this)

    applyStylesToElementAndChildren({
      element,
      $,
      styles: {
        font: selectedTheme.body_font,
        color: selectedTheme.body_color,
        style: selectedTheme.body_style,
        case: selectedTheme.body_case,
      },
      parseCSS,
      stringifyCSS,
    })
  })

  // Handle pure text elements that are not associated with case details
  $('p, span, div, td, th, li, strong, em, b, i').each(function () {
    const element = $(this)

    const hasDataAttribute =
      element.attr('data-template') ||
      element.attr('data-case-detail') ||
      element.attr('data-case-detail-table')

    const parentHasDataAttribute =
      element.closest(
        '[data-template], [data-case-detail], [data-case-detail-table]',
      ).length > 0

    if (!hasDataAttribute && !parentHasDataAttribute) {
      applyStylesToSingleElement({
        element,
        styles: {
          font: selectedTheme.body_font,
          color: selectedTheme.body_color,
          style: selectedTheme.body_style,
          case: selectedTheme.body_case,
        },
        parseCSS,
        stringifyCSS,
      })
    }
  })

  return $('body').html()
}

const applyStylesToElementAndChildren = ({
  element,
  $,
  styles,
  parseCSS,
  stringifyCSS,
}: ApplyStylesToElementAndChildrenProps): void => {
  applyStylesToSingleElement({
    element,
    styles,
    parseCSS,
    stringifyCSS,
  })

  const textElements = element.find(
    'p, span, div, td, th, li, strong, em, b, i',
  )

  textElements.each(function () {
    const childElement = $(this)
    applyStylesToSingleElement({
      element: childElement,
      styles,
      parseCSS,
      stringifyCSS,
    })
  })
}

/**
 * Helper to check if a style property should be preserved (not overwritten by theme).
 * Reads from the element's data-preserve-styles attribute which contains
 * a comma-separated list of CSS property names that should be preserved.
 */
const getPreservedStyles = (element: Cheerio<AnyNode>): Set<string> => {
  const preserveAttr = element.attr('data-preserve-styles')
  if (!preserveAttr) return new Set()
  return new Set(preserveAttr.split(',').map((s) => s.trim()))
}

const applyStylesToSingleElement = ({
  element,
  styles,
  parseCSS,
  stringifyCSS,
}: ApplyStylesToSingleElementProps): void => {
  const existingStyle = element.attr('style') || ''
  const styleObj = parseCSS(existingStyle)
  const preservedStyles = getPreservedStyles(element)

  // Apply font-family if not preserved by user. Append Noto Script Fallback
  // so theme-applied fonts get the same multi-script coverage as picker
  // selections — without this, a theme named "Helvetica" would write
  // `font-family: Helvetica` and Bengali pastes into the resulting span
  // would tofu in the PDF. See #2517.
  if (styles.font && !preservedStyles.has('font-family')) {
    styleObj['font-family'] = withScriptFallback(styles.font)
  }

  // Apply color if not preserved by user
  if (styles.color && !preservedStyles.has('color')) {
    styleObj['color'] = styles.color
  }

  // Apply font-weight and font-style if not preserved by user
  if (styles.style) {
    const normalStyle = { 'font-weight': 'normal', 'font-style': 'normal' }
    const boldStyle = { 'font-weight': 'bold', 'font-style': 'normal' }
    const italicStyle = { 'font-weight': 'normal', 'font-style': 'italic' }
    const boldItalicStyle = { 'font-weight': 'bold', 'font-style': 'italic' }
    const styleMap: Record<string, Record<string, string>> = {
      NORMAL: normalStyle,
      BOLD: boldStyle,
      ITALIC: italicStyle,
      'BOLD-ITALIC': boldItalicStyle,
    }

    const mappedStyles = styleMap[styles.style]
    if (mappedStyles) {
      // Only apply styles that are not preserved
      Object.entries(mappedStyles).forEach(([prop, value]) => {
        if (!preservedStyles.has(prop)) {
          styleObj[prop] = value
        }
      })
    }
  }

  // Apply text-transform if not preserved by user
  if (styles.case && !preservedStyles.has('text-transform')) {
    const caseMap: Record<string, string> = {
      NONE: 'none',
      UPPER: 'uppercase',
      LOWER: 'lowercase',
      CAPITALIZED: 'capitalize',
    }

    const textTransform = caseMap[styles.case]
    if (textTransform) {
      styleObj['text-transform'] = textTransform
    }
  }

  const newStyleString = stringifyCSS(styleObj)
  if (newStyleString) {
    element.attr('style', newStyleString)
  }
}

export default applyThemeStylesToServiceTemplate
